import { GridsterItemConfig, GridsterOptions, ResolvedGridsterOptions } from '../types';
import type { GridsterItemInstance } from './gridster-item-instance';
import {
  checkCollisionTwoItems,
  checkCollisionTwoItemsForSwapping,
  checkGridCollision as checkGridCollisionForItem,
  getNextPossiblePosition as getNextPossiblePositionForItems,
  pixelsToPosition
} from './gridster-layout';
import { GridsterRenderer } from './gridster-renderer';
import { resolveOptions } from './gridster-utils';

export class GridsterEngine {
  el: HTMLElement | null = null;
  previewEl: HTMLElement | null = null;
  options: ResolvedGridsterOptions;
  items: GridsterItemInstance[] = [];
  gridRenderer = new GridsterRenderer(this);
  columns = 1;
  rows = 1;
  curColWidth = 0;
  curRowHeight = 0;
  curWidth = 0;
  paddingLeft = 0;
  paddingTop = 0;
  movingItem: GridsterItemConfig | null = null;
  dragInProgress = false;
  onItemChange?: (items: GridsterItemConfig[]) => void;

  private resizeObserver: ResizeObserver | null = null;
  private pendingChanges = new Set<GridsterItemInstance>();
  private pendingItems: GridsterItemInstance[] = [];
  private pendingItemsScheduled = false;

  constructor(options: GridsterOptions, onItemChange?: (items: GridsterItemConfig[]) => void) {
    this.options = resolveOptions(options);
    this.onItemChange = onItemChange;
  }

  attach(el: HTMLElement, previewEl: HTMLElement): void {
    this.el = el;
    this.previewEl = previewEl;
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(el);
    this.calculateLayout();
  }

  detach(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.el = null;
    this.previewEl = null;
  }

  setOptions(options: GridsterOptions): void {
    this.options = resolveOptions(options);
    this.calculateLayout();
  }

  setOnItemChange(onItemChange?: (items: GridsterItemConfig[]) => void): void {
    this.onItemChange = onItemChange;
  }

  getItems(): GridsterItemConfig[] {
    return this.items.map(instance => instance.item);
  }

  addItem(item: GridsterItemInstance): void {
    const $item = item.$item;
    if ($item.cols === undefined || $item.cols === -1) {
      $item.cols = this.options.defaultItemCols;
      item.item.cols = $item.cols;
      item.itemChanged();
    }
    if ($item.rows === undefined || $item.rows === -1) {
      $item.rows = this.options.defaultItemRows;
      item.item.rows = $item.rows;
      item.itemChanged();
    }
    this.items.push(item);
    this.pendingItems.push(item);
    this.schedulePendingItems();
  }

  removeItem(item: GridsterItemInstance): void {
    this.items = this.items.filter(candidate => candidate !== item);
    this.pendingItems = this.pendingItems.filter(candidate => candidate !== item);
    this.pendingChanges.delete(item);
    if (this.movingItem === item.$item) {
      this.movingItem = null;
      this.previewStyle();
    }
    this.calculateLayout();
  }

  private schedulePendingItems(): void {
    if (this.pendingItemsScheduled) {
      return;
    }
    this.pendingItemsScheduled = true;
    queueMicrotask(() => {
      this.pendingItemsScheduled = false;
      this.flushPendingItems();
    });
  }

  private flushPendingItems(): void {
    const pendingItems = this.pendingItems;
    this.pendingItems = [];

    for (const item of pendingItems) {
      if (!this.items.includes(item)) {
        continue;
      }
      const $item = item.$item;
      if ($item.x === -1 || $item.y === -1) {
        this.autoPositionItem(item);
      } else if (this.checkCollision($item)) {
        this.autoPositionItem(item);
      }
    }

    this.calculateLayout();
    this.flushItemChanges();
  }

  itemChanged(item: GridsterItemInstance): void {
    this.pendingChanges.add(item);
  }

  updateItem(item: GridsterItemInstance, config: GridsterItemConfig): void {
    item.updateConfig(config);
  }

  flushItemChanges(): void {
    if (!this.pendingChanges.size) {
      return;
    }
    this.pendingChanges.clear();
    this.onItemChange?.(this.getItems());
  }

  calculateLayout(): void {
    const el = this.el;
    if (!el) {
      return;
    }

    this.setGridDimensions();

    const options = this.options;
    const outerLeft = options.outerMarginLeft ?? options.margin;
    const outerRight = options.outerMarginRight ?? options.margin;
    const outerTop = options.outerMarginTop ?? options.margin;
    const outerBottom = options.outerMarginBottom ?? options.margin;

    el.style.paddingLeft = `${outerLeft}px`;
    el.style.paddingRight = `${outerRight}px`;
    el.style.paddingTop = `${outerTop}px`;
    el.style.paddingBottom = `${outerBottom}px`;

    this.paddingLeft = outerLeft;
    this.paddingTop = outerTop;

    this.curColWidth = options.fixedColWidth + options.margin;
    this.curRowHeight = options.fixedRowHeight + options.margin;

    const height = this.rows * this.curRowHeight - options.margin + outerTop + outerBottom;
    el.style.height = `${Math.max(height, 0)}px`;

    for (const item of this.items) {
      item.setSize();
      item.drag.toggle();
      item.resize.toggle();
    }
  }

  updateGrid(): void {
    this.setGridDimensions();
  }

  onResize(): void {
    const el = this.el;
    if (!el || el.clientWidth === this.curWidth) {
      return;
    }
    this.calculateLayout();
  }

  checkCollision(item: GridsterItemConfig): GridsterItemInstance | true | false {
    if (this.checkGridCollision(item)) {
      return true;
    }
    return this.findItemWithItem(item) ?? false;
  }

  checkCollisionForSwapping(item: GridsterItemConfig): GridsterItemInstance | true | false {
    if (this.checkGridCollision(item)) {
      return true;
    }
    for (let i = this.items.length - 1; i > -1; i--) {
      const candidate = this.items[i];
      if (
        !candidate.notPlaced &&
        candidate.$item !== item &&
        checkCollisionTwoItemsForSwapping(candidate.$item, item)
      ) {
        return candidate;
      }
    }
    return false;
  }

  checkGridCollision(item: GridsterItemConfig): boolean {
    return checkGridCollisionForItem(item, this.options);
  }

  findItemWithItem(item: GridsterItemConfig): GridsterItemInstance | null {
    for (const candidate of this.items) {
      if (
        !candidate.notPlaced &&
        candidate.$item !== item &&
        checkCollisionTwoItems(candidate.$item, item)
      ) {
        return candidate;
      }
    }
    return null;
  }

  findItemsWithItem(item: GridsterItemConfig): GridsterItemInstance[] {
    return this.items.filter(
      candidate =>
        !candidate.notPlaced &&
        candidate.$item !== item &&
        checkCollisionTwoItems(candidate.$item, item)
    );
  }

  getNextPossiblePosition(
    newItem: GridsterItemConfig,
    startingFrom: { x?: number; y?: number } = {}
  ): boolean {
    this.setGridDimensions();
    return getNextPossiblePositionForItems(
      newItem,
      this.items.filter(instance => !instance.notPlaced).map(instance => instance.$item),
      this.options,
      { columns: this.columns, rows: this.rows },
      startingFrom
    );
  }

  getFirstPossiblePosition(item: GridsterItemConfig): GridsterItemConfig {
    const tmpItem = { ...item };
    this.getNextPossiblePosition(tmpItem);
    return tmpItem;
  }

  pixelsToPositionX(x: number, rounding: (value: number) => number): number {
    return pixelsToPosition(x, this.curColWidth, this.options.maxCols, rounding);
  }

  pixelsToPositionY(y: number, rounding: (value: number) => number): number {
    return pixelsToPosition(y, this.curRowHeight, this.options.maxRows, rounding);
  }

  positionXToPixels(x: number): number {
    return x * this.curColWidth;
  }

  positionYToPixels(y: number): number {
    return y * this.curRowHeight;
  }

  previewStyle(): void {
    const preview = this.previewEl;
    if (!preview) {
      return;
    }
    if (this.movingItem) {
      preview.style.display = 'block';
      this.gridRenderer.updateItem(preview, this.movingItem);
    } else {
      preview.style.display = '';
    }
  }

  private autoPositionItem(item: GridsterItemInstance): void {
    if (this.getNextPossiblePosition(item.$item)) {
      item.notPlaced = false;
      item.item.x = item.$item.x;
      item.item.y = item.$item.y;
      item.itemChanged();
    } else {
      item.notPlaced = true;
    }
  }

  private setGridDimensions(): void {
    const el = this.el;
    if (el) {
      this.curWidth = el.clientWidth;
    }

    let rows = this.options.minRows;
    let columns = this.options.minCols;

    for (const item of this.items) {
      if (!item.notPlaced) {
        rows = Math.max(rows, item.$item.y + item.$item.rows);
        columns = Math.max(columns, item.$item.x + item.$item.cols);
      }
    }

    this.columns = columns;
    this.rows = rows;
  }
}
